import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import TuneIcon from '@mui/icons-material/Tune';
import { useMemo } from 'react';
import { useUserRole } from 'app/hooks/useUserRole';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { NavigationItem } from 'app/shared-components/layout/navbar/NavBar';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { HighnoteProductType } from 'API';

export const useNavigationMenu = (filterById?: string) => {
  const {
    userHasAccessToCreditManagement,
    isAdministrator,
    isGroupAdmin: isGroupAdministrator,
    isCardholder: isActualCardholder,
    canManageUsersAndCards,
    canAccessSettings,
    canAccessTransactions,
    canAccessAdminReimbursements,
    canViewAuditLog,
    showsHomeIcon,
  } = useUserRole();
  const {
    'reimbursementsFlags.showReimbursements': showReimbursements,
    'flexCoding.enabled': isFlexCodingEnabled,
    'showFlexFieldSetting.enabled': isFlexFieldEnabled,
    'speedchatEnabled.enabled': showSpeedChat,
  } = useIsEnabled([
    { featureName: 'reimbursementsFlags', flagName: 'showReimbursements' },
    { featureName: 'flexCoding', flagName: 'enabled' },
    { featureName: 'showFlexFieldSetting', flagName: 'enabled' },
    { featureName: 'speedchatEnabled', flagName: 'enabled' },
  ]);

  const { t } = useTranslation();
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { selectedPaidol, user } = useAppSelector(selectUserCompanies);
  // SEE-265: Control Center is for Speedchain support staff. Gate on the User row's
  // is_support, not the per-company role — support users often aren't members of the
  // company they have selected.
  const isSupport = Boolean(user?.is_support);
  const isReimbursementsEnabled = useMemo(
    () => !!selectedPaidol?.reimbursementSettings?.isEnabled && showReimbursements,
    [selectedPaidol?.reimbursementSettings?.isEnabled, showReimbursements]
  );

  // Pre-SEE-946 the sidebar used a raw "not administrator" check for cardholder-only
  // tabs (My Transactions, My Cards). With three new roles in play we need an
  // explicit cardholder check rather than "anyone who isn't ADMINISTRATOR".
  const isCardholderOnly = isActualCardholder;

  const isConstructionType = useMemo(
    () => selectedPaidol?.isConstructionType ?? false,
    [selectedPaidol?.isConstructionType]
  );

  const isErpSpeedchain = useMemo(
    () => selectedPaidol?.erpSpeedchain ?? false,
    [selectedPaidol?.erpSpeedchain]
  );

  const isWelcomeDismissed = selectedPaidol?.firstRunChecklist?.dismissWelcomePage;

  const navigationElements: Array<NavigationItem> = useMemo(
    () =>
      [
        {
          id: 'overview',
          label: t('home'),
          href: '/overview',
          IconComponent: TrackChangesIcon,
          // SEE-946 / SEE-1081: the sidebar Home icon (and the whole Overview
          // section) is driven by showsHomeIcon from roleAccess.ts — the single
          // source of truth (see app/auth/roleAccess.ts), which also drives
          // RequireAuth's post-login redirect. Secondary admins now have it set
          // true (SEE-1081) so they reach the transactions-only Overview; the
          // per-tab gates below still hide everything except Transactions.
          hasAccess: showsHomeIcon,
          tabs: [
            {
              label: t('welcome'),
              href: '/overview',
              // SEE-946 acceptance update: Welcome page is Primary-only.
              // Secondary admins skip Welcome entirely (their default landing
              // is /team/members). Approval admins skip Welcome (their primary
              // surface is /overview/transactions).
              hasAccess: Boolean(isAdministrator && !isWelcomeDismissed),
            },
            {
              label: t('transactions.navigationMenuTitle'),
              href: '/overview/transactions',
              // SEE-946 / SEE-1081: transactions accessible to Primary, Group,
              // Approval, and now Secondary (visibility + review).
              hasAccess: canAccessTransactions,
            },
            {
              label: t('myTransactions'),
              href: '/overview/my-transactions',
              // SPE-3541: Group Admins keep their personal My Transactions tab.
              // SEE-946 narrowed this gate from "anyone who isn't Primary
              // Administrator" to "cardholder role only", which unintentionally
              // dropped Group Admins (the ticket's own AC says the group admin
              // role "will remain unchanged"). Restore them here, matching the
              // My Reimbursements tab below which kept its group-admin clause.
              // Primary and Approval admins use /profile/my-transactions;
              // Secondary admins have no personal-transactions surface.
              hasAccess: isCardholderOnly || isGroupAdministrator,
            },
            {
              label: t('myCards'),
              href: '/overview/my-cards',
              // SPE-3541: same SEE-946 regression as My Transactions above —
              // Group Admins lost their personal My Cards tab. Restore them.
              hasAccess: isCardholderOnly || isGroupAdministrator,
            },

            {
              label: t('insights'),
              href: '/overview/insights',
              // Insights stays Primary-only (not in the SEE-946 matrix).
              hasAccess: isAdministrator,
            },
            {
              label: t('myReimbursements'),
              href: '/overview/my-reimbursements',
              hasAccess: (isCardholderOnly || isGroupAdministrator) && isReimbursementsEnabled,
            },
            {
              label: t('reimbursements'),
              href: '/overview/reimbursements',
              // SEE-946: Approval admins approve reimbursements alongside
              // Primary per the matrix; canAccessAdminReimbursements covers both.
              hasAccess: Boolean(isReimbursementsEnabled && canAccessAdminReimbursements),
            },
          ],
        },
        {
          id: 'cards',
          label: t('cards'),
          href: '/cards',
          IconComponent: CreditCardIcon,
          // SEE-946: Cards section visible to Primary + Secondary. Approval/Group/Cardholder hidden.
          hasAccess: canManageUsersAndCards,
          tabs: [
            {
              label: t('cardManagement'),
              href: '/cards/payment-cards',
              hasAccess: canManageUsersAndCards,
            },
            {
              label: t('spendRulesTab'),
              href: '/cards/spend-rules',
              hasAccess: canManageUsersAndCards,
            },
            {
              label: t('manageCredit'),
              href: '/cards/credit',
              // Credit stays Primary-only.
              hasAccess: userHasAccessToCreditManagement,
            },
            {
              label: t('debitFunding'),
              href: '/cards/funding',
              hasAccess:
                canManageUsersAndCards && integration?.highnoteProduct?.type === HighnoteProductType.DEBIT,
            },
          ],
        },
        {
          id: 'team',
          label: 'Organization',
          href: '/team',
          IconComponent: PeopleIcon,
          // Organization is visible to everyone — Approval admins use Team
          // Members read-only to identify cardholders / submitters when
          // reviewing transactions; Cardholders / Group admins have their
          // card-group + speedchat surfaces here.
          hasAccess: true,
          tabs: [
            {
              label: t('teamMembers'),
              href: '/team/members',
              // Visible to every role — write actions (Invite / Edit /
              // Delete) are gated inside Users.tsx by canManageUsersAndCards
              // so Approval / Group / Cardholder get the read-only listing
              // they need without the management controls.
              hasAccess: true,
            },
            {
              label: t('navigationTabs.cardGroups'),
              href: '/team/card-groups',
              // SEE-946: card groups belong to user/card management.
              hasAccess: Boolean(canManageUsersAndCards || isGroupAdministrator),
            },
            {
              label: t('navigationTabs.projectGroups'),
              href: '/team/project-groups',
              hasAccess: Boolean(canManageUsersAndCards || isGroupAdministrator),
            },
            {
              label: t('navigationTabs.auditLog'),
              href: '/team/audit-log',
              // Primary admins and support users; the CDK query enforces the same rule.
              hasAccess: canViewAuditLog,
            },
            {
              label: 'Speedchat',
              href: '/team/speedchat',
              hasAccess: isCardholderOnly && showSpeedChat,
            },
          ],
        },
        {
          id: 'settings',
          label: t('settings'),
          href: '/settings',
          IconComponent: SettingsIcon,
          // SEE-946: settings visible to Primary + Secondary.
          hasAccess: canAccessSettings,
          tabs: [
            {
              label: t('appSettings'),
              href: '/settings/app-settings',
              hasAccess: canAccessSettings,
            },
            {
              label: t('connections'),
              href: '/settings/connectivity',
              hasAccess: canAccessSettings && (isConstructionType || isErpSpeedchain),
            },
            {
              label: 'Job Codes',
              href: '/settings/job-codes',
              hasAccess:
                !isConstructionType && !isFlexCodingEnabled && !!selectedPaidol?.jobCodeSettings?.isEnabled,
            },
            {
              label: t('flexCoding.title'),
              href: '/settings/flex-coding',
              hasAccess:
                !isConstructionType && isFlexCodingEnabled && !!selectedPaidol?.flexCodingSettings?.isEnabled,
            },
            {
              label: 'Flex builder',
              href: '/settings/data-fields',
              hasAccess:
                !isConstructionType && isFlexFieldEnabled && !!selectedPaidol?.flexFieldSettings?.isEnabled,
            },
            {
              label: 'Authentication',
              href: '/settings/authentication',
              hasAccess: canAccessSettings,
            },
          ],
        },
        {
          id: 'control-center',
          label: t('controlCenter'),
          href: '/control-center',
          IconComponent: TuneIcon,
          hasAccess: isSupport,
          tabs: [
            {
              label: t('controlCenterTabs.creditLimit'),
              href: '/control-center/credit-limit',
              hasAccess: isSupport,
            },
          ],
        },
      ].filter((f) => f.hasAccess || f.hasAccess === undefined),
    [
      t,
      isCardholderOnly,
      selectedPaidol?.jobCodeSettings?.isEnabled,
      selectedPaidol?.flexCodingSettings?.isEnabled,
      selectedPaidol?.flexFieldSettings?.isEnabled,
      isAdministrator,
      showsHomeIcon,
      canManageUsersAndCards,
      canAccessSettings,
      canAccessTransactions,
      canAccessAdminReimbursements,
      showReimbursements,
      isConstructionType,
      isErpSpeedchain,
      isWelcomeDismissed,
      isGroupAdministrator,
      isReimbursementsEnabled,
      integration?.highnoteProduct?.type,
      isFlexCodingEnabled,
      isFlexFieldEnabled,
      showSpeedChat,
      userHasAccessToCreditManagement,
      isSupport,
    ]
  );

  const filteredTabs = useMemo(
    () =>
      filterById
        ? navigationElements
            .find((f) => filterById === f.id)
            ?.tabs?.filter((f) => f.hasAccess || f.hasAccess === undefined) || []
        : [],
    [filterById, navigationElements]
  );

  return { navigationElements, filteredTabs };
};
